import { Button } from '@mui/material';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	addChangesHistory,
	createTicket,
	deleteTicket,
	getTicketsByUserId,
} from 'store/ticket-service/asyncActions';
import {
	getActiveTicket,
	getIsTicketUpdate,
	getTicketUpdating,
	getTickets,
} from 'store/ticket-service/selector';
import {
	addLocalTicket,
	removeLocalTicket,
	setActiveTicket,
	setIsTicketUpdate,
} from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';

import { HistoryActionTypes, NamespacesList, TicketStages } from 'core/enums/common';

import { InfoModal } from 'components/shared/Modal/Info';

import { ReactComponent as Error } from 'assets/icons/error.svg';

type CreateTicketModalProps = {
	handleCloseModal: () => void;
	ticket: TicketType;
	userId: string;
	isCreating?: boolean;
	onActionComplete?: () => void;
};

export const CreateTicketModal: FC<CreateTicketModalProps> = ({
	handleCloseModal,
	ticket,
	userId,
	isCreating,
	onActionComplete,
}) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const [open, setOpen] = useState<boolean>(true);
	const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
	const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
	// Référence pour suivre si une action utilisateur a été déclenchée explicitement
	const userActionTriggeredRef = useRef<boolean>(false);
	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener } = useEventBus();

	const updating = useSelector(getTicketUpdating);
	const tickets = useSelector(getTickets);
	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);

	const handleClose = useCallback(() => {
		if (!isConfirmLoading && !isCancelLoading) {
			handleCloseModal();
			setOpen(false);
			// Réinitialiser la référence lors de la fermeture
			userActionTriggeredRef.current = false;
		}
	}, [isConfirmLoading, isCancelLoading, handleCloseModal]);

	useEffect(() => {
		const handleSuccessfulSave = () => {
			// Ne traiter l'événement successfulSave que si une action utilisateur a été déclenchée
			// Cela empêche les déclenchements automatiques non désirés
			if (!userActionTriggeredRef.current) {
				return;
			}
			
			setIsConfirmLoading(false);
			setTimeout(() => {
				handleCloseModal();
				setOpen(false);
				onActionComplete?.();
				// Réinitialiser la référence après l'action
				userActionTriggeredRef.current = false;
			}, 100);
		};
		
		addEventListener('successfulSave', handleSuccessfulSave);

		return () => {
			removeEventListener('successfulSave', handleSuccessfulSave);
		};
	}, [handleCloseModal, onActionComplete]);

	const handleSave = () => {
		// S'assurer que cette action est déclenchée uniquement par un clic utilisateur explicite
		// Empêcher les déclenchements automatiques
		if (isConfirmLoading || isCancelLoading) {
			return;
		}
		
		// Marquer qu'une action utilisateur a été déclenchée
		userActionTriggeredRef.current = true;
		
		if (activeTicket) {
			if (!activeTicket.id) {
				setIsConfirmLoading(true);
				
				// S'assurer que le spinner s'affiche avant de lancer l'opération
				setTimeout(() => {
					dispatch(createTicket({ userId, ticket }))
						.then(() => dispatch(getTicketsByUserId({ userId })))
						.then((res: any) => {
							dispatch(setActiveTicket(res?.payload?.data[0]));

							dispatch(
								addChangesHistory({
									userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
									ticketId: res.payload.data[0].id,
									ticketStep: TicketStages.general,
									actionType: HistoryActionTypes.ticketCreate,
								})
							);

							isCreating && createNewLocalTicket();
							setIsConfirmLoading(false);
							handleClose();
							onActionComplete?.();
						})
						.catch(() => {
							setIsConfirmLoading(false);
						});
				}, 0);
			} else {
				if (isTicketUpdate) {
					setIsConfirmLoading(true);
					dispatch(triggerTicketSave());
					// Le spinner sera réinitialisé par l'event 'successfulSave'
				} else {
					handleClose();
					onActionComplete?.();
				}
			}
		}
	};

	const createNewLocalTicket = () => {
		const defaultTicket = {
			title: 'New request',
			description: '',
			createdDate: new Date().toISOString(),
			comment: null,
			messages: [],
			status: 0,
			currentStep: TicketStages.general,
			ticketSummaries: [],
			userStoryTests: [],
			useCases: [],
		};
		dispatch(addLocalTicket(defaultTicket));
		dispatch(setActiveTicket(defaultTicket));
	};

	const handleDelete = async () => {
		// S'assurer que cette action est déclenchée uniquement par un clic utilisateur explicite
		// Empêcher les déclenchements automatiques
		if (isConfirmLoading || isCancelLoading) {
			return;
		}
		
		// Marquer qu'une action utilisateur a été déclenchée
		userActionTriggeredRef.current = true;
		
		if (activeTicket) {
			setIsCancelLoading(true);
			
			// S'assurer que le spinner s'affiche avant de lancer l'opération
			setTimeout(async () => {
				if (!activeTicket.id) {
					dispatch(removeLocalTicket());
					setIsCancelLoading(false);
					handleClose();
					onActionComplete?.();
				} else {
					try {
						// @ts-ignore
						await dispatch(deleteTicket(activeTicket.id));

						const recordsPerPage = tickets.length;

						await dispatch(getTicketsByUserId({ userId, recordsPerPage }));
						setIsCancelLoading(false);
						handleClose();
						dispatch(setIsTicketUpdate(false));
						onActionComplete?.();
					} catch (error) {
						setIsCancelLoading(false);
					}
				}
			}, 0);
		}
	};

	return (
		<InfoModal
			isOpen={open}
			onClose={handleClose}
			image={<Error />}
			title={modalTranslate('createTicketModal.title')}
			description={modalTranslate('createTicketModal.description')}
			buttons={
				<>
					<Button
						sx={{
							height: '44px',
							width: { xs: '45%', sm: '215px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="outlined"
						color="error"
						disabled={isConfirmLoading || isCancelLoading || updating}
						onClick={handleSave}
					>
						{isConfirmLoading ? (
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#6e6e6e"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							modalTranslate('createTicketModal.confirm')
						)}
					</Button>

					<Button
						sx={{
							height: '44px',
							width: { xs: '45%', sm: '215px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="contained"
						color="error"
						disabled={isConfirmLoading || isCancelLoading || updating}
						onClick={handleDelete}
					>
						{isCancelLoading ? (
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#6e6e6e"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						) : (
							modalTranslate('createTicketModal.cancel')
						)}
					</Button>
				</>
			}
		></InfoModal>
	);
};
